import { z } from 'zod';
import { paginationQuerySchema } from './pagination';
import { ORDER_STATUSES, SHIPMENT_STATUSES, TRACKING_STATES } from './shipment-status';

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD.')
  .optional();

const uuid = z.uuid().optional();

export const ORDER_SORTS = [
  'orderedAt',
  'totalMinor',
  'orderNumber',
  'customerName',
  'status',
] as const;
export const ordersQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100).optional(),
  status: z.enum(ORDER_STATUSES).optional(),
  shipmentStatus: z.enum(SHIPMENT_STATUSES).optional(),
  storeId: uuid,
  from: isoDate,
  to: isoDate,
  sort: z
    .string()
    .regex(new RegExp(`^-?(${ORDER_SORTS.join('|')})$`))
    .default('-orderedAt'),
});
export type OrdersQuery = z.infer<typeof ordersQuerySchema>;

export const SHIPMENT_SORTS = [
  'createdAt',
  'status',
  'estimatedDeliveryAt',
  'lastEventAt',
  'trackingNumber',
] as const;
export const shipmentsQuerySchema = paginationQuerySchema.extend({
  q: z.string().trim().max(100).optional(),
  status: z.enum(SHIPMENT_STATUSES).optional(),
  trackingState: z.enum(TRACKING_STATES).optional(),
  carrier: z.string().max(40).optional(),
  storeId: uuid,
  attention: z.enum(['true', 'false']).optional(),
  from: isoDate,
  to: isoDate,
  sort: z
    .string()
    .regex(new RegExp(`^-?(${SHIPMENT_SORTS.join('|')})$`))
    .default('-createdAt'),
});
export type ShipmentsQuery = z.infer<typeof shipmentsQuerySchema>;

export const analyticsQuerySchema = z.object({
  from: isoDate,
  to: isoDate,
  storeId: uuid,
});

const money = z.coerce.number().min(0).max(100_000_000);

/** Manual order entry (for MANUAL stores, or testing without a platform). */
export const createOrderSchema = z.object({
  storeId: z.uuid(),
  orderNumber: z.string().trim().min(1).max(40),
  customerName: z.string().trim().min(1).max(120),
  customerEmail: z.string().trim().toLowerCase().pipe(z.email('Enter a valid email address.')),
  customerPhone: z.string().trim().max(40).optional(),
  currency: z.string().length(3).toUpperCase(),
  orderedAt: z.iso.datetime().optional(),
  shippingAddress: z
    .object({
      name: z.string().max(120).optional(),
      line1: z.string().max(200).optional(),
      line2: z.string().max(200).optional(),
      city: z.string().max(100).optional(),
      region: z.string().max(100).optional(),
      postalCode: z.string().max(20).optional(),
      country: z.string().length(2).toUpperCase().optional(),
    })
    .optional(),
  items: z
    .array(
      z.object({
        title: z.string().trim().min(1).max(200),
        sku: z.string().max(64).optional(),
        variantTitle: z.string().max(120).optional(),
        quantity: z.coerce.number().int().min(1).max(10_000),
        unitPrice: money,
      }),
    )
    .min(1, 'Add at least one item.')
    .max(100),
});
export type CreateOrderInput = z.infer<typeof createOrderSchema>;

export const createShipmentSchema = z.object({
  orderId: z.uuid(),
  carrierCode: z.string().min(2).max(40),
  trackingNumber: z
    .string()
    .trim()
    .min(4, 'Tracking number looks too short.')
    .max(60)
    .regex(/^[A-Za-z0-9-]+$/, 'Use letters, digits and dashes only.'),
});
export type CreateShipmentInput = z.infer<typeof createShipmentSchema>;
